import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, CaretDown, MagnifyingGlass, Question, TestTube, Brain, ShieldCheck, User, ChartLine, Heartbeat } from '@phosphor-icons/react';

const faqCategories = [
  {
    id: 'general',
    name: 'General Questions',
    icon: Question,
    color: '#5B21B6',
    questions: [
      {
        q: "What is MenoPath?",
        a: "MenoPath is a science-driven platform that combines hormone biomarker analysis with lifestyle factors to provide personalized menopause timeline predictions. Using AI technology, we help women understand when they might enter menopause and what factors influence their transition."
      },
      {
        q: "Who is MenoPath for?",
        a: "MenoPath is designed for women aged 35-58 who want to better understand their menopause timeline. Whether you're experiencing early symptoms, planning for the future, or simply curious about your body's changes, MenoPath provides valuable insights."
      },
      {
        q: "Is MenoPath a replacement for my doctor?",
        a: "No. MenoPath is an informational tool designed to complement, not replace, professional medical advice. Our predictions and insights are for educational purposes only. Always consult with your healthcare provider for medical decisions, diagnosis, or treatment."
      },
      {
        q: "How accurate are the predictions?",
        a: "Our predictions are based on peer-reviewed research and validated biomarkers. While no prediction can be 100% accurate due to individual variation, our AI model considers multiple factors including hormone levels (AMH, FSH, Estradiol, etc.) and lifestyle factors to provide the most informed estimate possible."
      },
      {
        q: "Is MenoPath available internationally?",
        a: "Yes, MenoPath is accessible worldwide. However, hormone testing and lab work should be done through your local healthcare providers. Our platform interprets standard hormone measurements used globally."
      }
    ]
  },
  {
    id: 'hormones',
    name: 'Hormones & Testing',
    icon: TestTube,
    color: '#059669',
    questions: [
      {
        q: "What hormones does MenoPath analyze?",
        a: "We analyze six key reproductive hormones: FSH (Follicle Stimulating Hormone), LH (Luteinizing Hormone), Estradiol (E2), AMH (Anti-Müllerian Hormone), Inhibin B, and Progesterone. Each provides unique insights into ovarian function and reserve."
      },
      {
        q: "What is AMH and why is it important?",
        a: "AMH (Anti-Müllerian Hormone) is one of the most reliable markers of ovarian reserve. It's produced by cells in your ovarian follicles and remains relatively stable throughout your menstrual cycle. Lower AMH levels typically indicate fewer remaining eggs and may predict earlier menopause."
      },
      {
        q: "What does elevated FSH mean?",
        a: "FSH (Follicle Stimulating Hormone) rises when your ovaries need more stimulation to produce eggs. Consistently elevated FSH levels (typically above 25-40 mIU/mL) can indicate declining ovarian function and approaching menopause. However, FSH can fluctuate, so trends over time are more meaningful than single readings."
      },
      {
        q: "When should I get my hormones tested?",
        a: "For the most accurate results, hormone tests are typically done on days 2-5 of your menstrual cycle (early follicular phase). AMH can be tested any time as it's relatively stable. If you're experiencing irregular cycles, consult your doctor about optimal timing."
      },
      {
        q: "How often should I update my hormone data?",
        a: "We recommend updating your hormone data every 6-12 months, or whenever you have new lab results. This allows our AI to track trends and refine predictions over time. More frequent updates during perimenopause can be particularly valuable."
      },
      {
        q: "Can I use home hormone tests?",
        a: "While some at-home hormone tests are becoming available, we recommend using results from accredited laboratories through your healthcare provider for the most accurate readings. Lab-grade testing provides the precision needed for meaningful predictions."
      }
    ]
  },
  {
    id: 'lifestyle',
    name: 'Lifestyle Factors',
    icon: Heartbeat,
    color: '#DC2626',
    questions: [
      {
        q: "How do lifestyle factors affect menopause timing?",
        a: "Research shows that lifestyle factors can influence menopause timing by 1-3 years. Smoking is associated with earlier menopause (1-2 years), while factors like regular exercise, healthy BMI, and moderate alcohol consumption may support later menopause. Our algorithm weighs these factors alongside your hormone levels."
      },
      {
        q: "Does smoking really affect menopause?",
        a: "Yes, significantly. Studies consistently show that current smokers experience menopause 1-2 years earlier than non-smokers. The chemicals in cigarettes are toxic to ovarian follicles and accelerate egg depletion. Former smokers have a smaller but still measurable effect."
      },
      {
        q: "How does BMI influence menopause?",
        a: "Body composition affects hormone metabolism. Very low BMI (underweight) is associated with earlier menopause, while moderate-to-higher BMI may be associated with slightly later menopause due to estrogen production in fat tissue. However, extreme obesity can disrupt hormonal balance in other ways."
      },
      {
        q: "Does exercise affect menopause timing?",
        a: "Regular moderate exercise is associated with overall hormonal health and may support normal menopause timing. However, excessive exercise that leads to very low body fat can disrupt menstrual cycles and potentially affect menopause. Balance is key."
      },
      {
        q: "What about alcohol consumption?",
        a: "Light to moderate alcohol consumption doesn't appear to significantly affect menopause timing. However, heavy alcohol use can disrupt hormonal balance and overall health. We ask about alcohol as part of a complete lifestyle picture."
      },
      {
        q: "Can stress affect menopause?",
        a: "While acute stress doesn't directly cause early menopause, chronic severe stress can affect hormonal balance and may influence your overall health during the transition. Managing stress through sleep, exercise, and self-care supports healthier aging."
      }
    ]
  },
  {
    id: 'predictions',
    name: 'Predictions & Results',
    icon: ChartLine,
    color: '#2563EB',
    questions: [
      {
        q: "What is the MMI (My MenoPath Index)?",
        a: "The MMI is our proprietary index that visualizes your estimated position on the menopause timeline. It combines your hormone levels and lifestyle factors into a single score that tracks your progression toward menopause, displayed against benchmark curves for different menopause ages."
      },
      {
        q: "What do the benchmark curves on the chart mean?",
        a: "The benchmark curves (ages 48-53) represent typical MMI decline patterns for women who reach menopause at those ages. By comparing your data to these curves, you can see which trajectory most closely matches your current profile."
      },
      {
        q: "Why does my prediction show a range instead of a specific age?",
        a: "Menopause prediction inherently involves uncertainty. We provide a range (e.g., 49-52) to honestly represent this uncertainty. The range is based on your hormone levels, lifestyle factors, and the natural variability in menopause timing. A narrower range indicates higher confidence."
      },
      {
        q: "Can my prediction change over time?",
        a: "Yes, and it should! As you update your hormone data and lifestyle information, our AI refines its predictions. Significant changes in your health, weight, smoking status, or hormone levels will be reflected in updated predictions."
      },
      {
        q: "What if my prediction doesn't match my symptoms?",
        a: "Symptoms can begin years before actual menopause (perimenopause) and vary greatly between women. Our prediction focuses on when menopause (12 months without periods) is likely to occur. Symptom management is a separate consideration best discussed with your healthcare provider."
      },
      {
        q: "What stages of menopause does MenoPath identify?",
        a: "We identify several stages: Pre-perimenopause (stable hormones), Early Perimenopause (beginning hormonal changes), Late Perimenopause (significant changes, irregular cycles), and Post-menopause (12+ months without periods). Your stage is determined by your hormone patterns and age."
      }
    ]
  },
  {
    id: 'privacy',
    name: 'Privacy & Security',
    icon: ShieldCheck,
    color: '#059669',
    questions: [
      {
        q: "How is my health data protected?",
        a: "Your data is encrypted both in transit (TLS/SSL) and at rest. We use industry-standard security practices, including secure authentication, database encryption, and strict access controls. Your health information is treated with the utmost care."
      },
      {
        q: "Do you sell my data?",
        a: "Absolutely not. We will never sell, share, or monetize your personal health data with advertisers, data brokers, or any third parties for marketing purposes. Your data is used solely to provide you with predictions and insights."
      },
      {
        q: "Who can see my information?",
        a: "Only you can see your personal health data and predictions. Our technical team may access aggregated, anonymized data for improving our algorithms, but individual records are not accessed without your explicit consent or legal requirement."
      },
      {
        q: "Can I delete my data?",
        a: "Yes. You can request complete deletion of your account and all associated data at any time by contacting us at privacy@menopath.ai. We will delete your information within 30 days, except where retention is legally required."
      },
      {
        q: "Is my data shared with insurance companies?",
        a: "No. We do not share your data with insurance companies, employers, or any entity that could use your health information against your interests. Your menopause predictions and health data remain completely private."
      }
    ]
  },
  {
    id: 'account',
    name: 'Account & Access',
    icon: User,
    color: '#92400E',
    questions: [
      {
        q: "Why do I need to request early access?",
        a: "We're currently in beta and carefully onboarding users to ensure the best experience. This allows us to provide personalized support, gather feedback, and continuously improve our platform. Approved users get full access to all features."
      },
      {
        q: "How long until I get approved?",
        a: "Most access requests are reviewed within 5-7 business days. We're expanding capacity regularly, so wait times may vary. You'll receive an email notification when your access is approved."
      },
      {
        q: "Is MenoPath free?",
        a: "During our beta period, MenoPath is free for approved users. We're focused on building the best possible product and gathering feedback. Future pricing will be announced before the end of the beta period."
      },
      {
        q: "Can I use MenoPath on my phone?",
        a: "Yes! MenoPath is fully responsive and works on mobile browsers. We're also developing native iOS and Android apps—stay tuned for announcements about our mobile app launch."
      },
      {
        q: "How do I update my information?",
        a: "You can update your hormone data and lifestyle information anytime from your dashboard. Click on 'Update Blood Test' or 'Update Lifestyle' to enter new data. Your predictions will automatically recalculate with the new information."
      },
      {
        q: "I forgot my password. What do I do?",
        a: "Click 'Forgot Password?' on the login page and enter your email address. We'll send you a secure link to reset your password. The link expires in 1 hour for security reasons. If you don't receive the email, check your spam folder or contact support@menopath.ai."
      }
    ]
  },
  {
    id: 'science',
    name: 'Science & Research',
    icon: Brain,
    color: '#7C3AED',
    questions: [
      {
        q: "What research is MenoPath based on?",
        a: "Our predictions are based on peer-reviewed studies on ovarian aging, hormone biomarkers, and menopause timing. Key research includes the SWAN (Study of Women's Health Across the Nation), Melbourne Women's Midlife Health Project, and numerous studies on AMH as a predictor of menopause."
      },
      {
        q: "How does the AI prediction work?",
        a: "Our AI model analyzes your hormone levels and lifestyle factors against patterns from research data. It considers the relationships between different hormones, your age, and lifestyle factors to estimate your menopause timeline. The model continuously learns and improves."
      },
      {
        q: "Is AMH really a good predictor of menopause?",
        a: "Yes, AMH is currently considered one of the best biomarkers for predicting menopause timing. Studies show it can predict menopause within a 3-4 year window for most women. Combined with FSH and other markers, accuracy improves further."
      },
      {
        q: "Why do you use multiple hormones instead of just one?",
        a: "Each hormone provides different information about ovarian function. AMH reflects ovarian reserve, FSH shows how hard your body is working to stimulate follicles, Estradiol indicates current ovarian output, and Inhibin B provides additional reserve information. Together, they paint a more complete picture."
      },
      {
        q: "How is MenoPath different from other menopause apps?",
        a: "Most menopause apps focus on symptom tracking. MenoPath uniquely combines actual hormone biomarker data with lifestyle factors and AI analysis to predict your menopause timing. We focus on the science of when, not just the symptoms of now."
      }
    ]
  }
];

export default function FAQ() {
  const [activeCategory, setActiveCategory] = useState('general');
  const [openQuestions, setOpenQuestions] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  const toggleQuestion = (categoryId, index) => {
    const key = `${categoryId}-${index}`;
    setOpenQuestions(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const filteredCategories = faqCategories.map(category => ({
    ...category,
    questions: category.questions.filter(
      q => q.q.toLowerCase().includes(searchTerm.toLowerCase()) ||
           q.a.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(category => category.questions.length > 0);

  const activeData = searchTerm 
    ? filteredCategories 
    : faqCategories.filter(c => c.id === activeCategory);

  return (
    <div className="min-h-screen bg-[#F8F6FF]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/70 backdrop-blur-xl border-b border-white/40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold text-[#5B21B6] font-['Outfit']" data-testid="nav-logo">
            MenoPath
          </Link>
          <Link to="/">
            <button className="flex items-center gap-2 text-[#5B21B6] hover:text-[#4C1D95] transition-colors font-medium">
              <ArrowLeft className="w-5 h-5" />
              Back to Home
            </button>
          </Link>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-28 pb-12 px-6 bg-gradient-to-br from-[#5B21B6] to-[#7C3AED] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl" />
        </div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white font-['Outfit'] leading-tight">
              Frequently Asked Questions
            </h1>
            <p className="mt-4 text-xl text-white/90 max-w-2xl mx-auto">
              Everything you need to know about MenoPath, menopause predictions, and your health journey.
            </p>
            
            {/* Search Bar */}
            <div className="mt-8 max-w-xl mx-auto relative">
              <MagnifyingGlass className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B7280]" />
              <input
                type="text"
                placeholder="Search questions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-4 rounded-2xl bg-white/95 text-[#1E1B4B] placeholder-[#6B7280] focus:outline-none focus:ring-2 focus:ring-white/50 shadow-lg"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <main className="py-12 px-6">
        <div className="max-w-6xl mx-auto">
          {!searchTerm && (
            <div className="flex flex-wrap justify-center gap-3 mb-10">
              {faqCategories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`flex items-center gap-2 px-5 py-3 rounded-full font-medium transition-all duration-300 ${
                    activeCategory === category.id
                      ? 'bg-[#5B21B6] text-white shadow-lg shadow-purple-200'
                      : 'bg-white text-[#374151] hover:bg-[#EDE9FE] border border-gray-200'
                  }`}
                >
                  <category.icon className="w-5 h-5" weight="duotone" />
                  {category.name}
                </button>
              ))}
            </div>
          )}

          {/* FAQ Accordion */}
          <div className="space-y-6">
            {activeData.map((category) => (
              <div key={category.id}>
                {searchTerm && (
                  <div className="flex items-center gap-2 mb-4">
                    <category.icon className="w-6 h-6" style={{ color: category.color }} weight="duotone" />
                    <h2 className="text-xl font-bold text-[#1E1B4B] font-['Outfit']">{category.name}</h2>
                  </div>
                )}
                <div className="space-y-3">
                  {category.questions.map((item, index) => {
                    const isOpen = openQuestions[`${category.id}-${index}`];
                    return (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                      >
                        <button
                          onClick={() => toggleQuestion(category.id, index)}
                          className="w-full flex items-center justify-between p-5 text-left"
                        >
                          <span className="font-semibold text-[#1E1B4B] pr-4">{item.q}</span>
                          <CaretDown 
                            className={`w-5 h-5 text-[#5B21B6] flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} 
                            weight="bold"
                          />
                        </button>
                        <AnimatePresence>
                          {isOpen && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.3 }}
                              className="overflow-hidden"
                            >
                              <div className="px-5 pb-5 pt-0">
                                <div className="h-px bg-gray-100 mb-4" />
                                <p className="text-[#6B7280] leading-relaxed">{item.a}</p>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Still Have Questions */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16 text-center"
          >
            <div className="bg-gradient-to-br from-[#EDE9FE] to-[#D1FAE5] rounded-3xl p-8 md:p-12">
              <h2 className="text-2xl font-bold text-[#1E1B4B] font-['Outfit'] mb-4">
                Still Have Questions?
              </h2>
              <p className="text-[#6B7280] mb-6 max-w-xl mx-auto">
                Can't find what you're looking for? Our support team is here to help you with any questions about MenoPath.
              </p>
              <Link to="/contact">
                <button className="bg-[#5B21B6] hover:bg-[#4C1D95] text-white font-semibold px-8 py-4 rounded-full transition-colors shadow-lg">
                  Contact Support
                </button>
              </Link>
            </div>
          </motion.div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#1E1B4B] py-8 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-white/50 text-sm">© 2026 MenoPath. All rights reserved.</p>
          <div className="mt-4 flex flex-wrap justify-center gap-6">
            <Link to="/about" className="text-white/70 hover:text-white transition-colors text-sm">About</Link>
            <Link to="/contact" className="text-white/70 hover:text-white transition-colors text-sm">Contact</Link>
            <Link to="/faq" className="text-white/70 hover:text-white transition-colors text-sm">FAQ</Link>
            <Link to="/terms" className="text-white/70 hover:text-white transition-colors text-sm">Terms of Service</Link>
            <Link to="/privacy" className="text-white/70 hover:text-white transition-colors text-sm">Privacy Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
